// covered-by: frontends/carrie/src/ask-v2/home/__tests__/AskV2Home.test.tsx
import cx from "classnames";
import {
  forwardRef,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import { useTranslation } from "react-i18next";

export type AskV2ComposerMode = "chat" | "project";

const HOME_COMPOSER_SLIDE_EASING = "cubic-bezier(0.16, 1, 0.3, 1)" as const;
const MODES: AskV2ComposerMode[] = ["chat", "project"];
const TRACK_INSET_PX = 2;

export function AskV2ComposerModeToggle({
  value,
  onChange,
  "data-testid": dataTestId = "ask-v2-composer-mode",
}: {
  value: AskV2ComposerMode;
  onChange: (mode: AskV2ComposerMode) => void;
  "data-testid"?: string;
}) {
  const { t } = useTranslation("ui");
  const trackRef = useRef<HTMLDivElement>(null);
  const chatRef = useRef<HTMLButtonElement>(null);
  const projectRef = useRef<HTMLButtonElement>(null);
  const [thumb, setThumb] = useState({ left: 0, width: 0 });

  useLayoutEffect(() => {
    const track = trackRef.current;
    const activeEl = value === "chat" ? chatRef.current : projectRef.current;
    if (!track || !activeEl) return;

    const measure = () => {
      const trackBox = track.getBoundingClientRect();
      const activeBox = activeEl.getBoundingClientRect();
      const segLeft = activeBox.left - trackBox.left;
      const segRight = activeBox.right - trackBox.left;
      const left = Math.max(TRACK_INSET_PX, segLeft);
      const right = Math.min(trackBox.width - TRACK_INSET_PX, segRight);
      setThumb({
        left,
        width: Math.max(0, right - left),
      });
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(track);
    observer.observe(activeEl);
    return () => observer.disconnect();
  }, [value]);

  const selectMode = (mode: AskV2ComposerMode) => {
    if (mode === value) return;
    onChange(mode);
    (mode === "chat" ? chatRef.current : projectRef.current)?.focus();
  };

  const handleSegmentKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    const index = MODES.indexOf(value);
    if (index < 0) return;
    let next: AskV2ComposerMode | null = null;
    switch (event.key) {
      case "ArrowRight":
      case "ArrowDown":
        next = MODES[(index + 1) % MODES.length] ?? null;
        break;
      case "ArrowLeft":
      case "ArrowUp":
        next = MODES[(index - 1 + MODES.length) % MODES.length] ?? null;
        break;
      case "Home":
        next = "chat";
        break;
      case "End":
        next = "project";
        break;
      default:
        break;
    }
    if (next == null) return;
    event.preventDefault();
    selectMode(next);
  };

  return (
    <div
      ref={trackRef}
      role="radiogroup"
      aria-label={t("home.composerModeAriaLabel")}
      data-testid={dataTestId}
      className="relative inline-flex h-8 items-stretch overflow-hidden rounded-xl bg-puntt-neutral-gray-3 p-0.5"
    >
      <span
        aria-hidden="true"
        data-testid="ask-v2-composer-mode-thumb"
        className="pointer-events-none absolute inset-y-0.5 z-[1] rounded-[10px] bg-base-white shadow-sm motion-reduce:transition-none"
        style={{
          left: thumb.left,
          width: thumb.width,
          transition: `left 220ms ${HOME_COMPOSER_SLIDE_EASING}, width 220ms ${HOME_COMPOSER_SLIDE_EASING}`,
        }}
      />
      <ModeSegment
        ref={chatRef}
        data-testid="ask-v2-mode-chat"
        label={t("home.composerModeChat")}
        active={value === "chat"}
        onSelect={() => onChange("chat")}
        onKeyDown={handleSegmentKeyDown}
      />
      <ModeSegment
        ref={projectRef}
        data-testid="ask-v2-mode-project"
        label={t("home.composerModeProject")}
        active={value === "project"}
        onSelect={() => onChange("project")}
        onKeyDown={handleSegmentKeyDown}
      />
    </div>
  );
}

const ModeSegment = forwardRef<
  HTMLButtonElement,
  {
    "data-testid": string;
    label: string;
    active: boolean;
    onSelect: () => void;
    onKeyDown: (event: KeyboardEvent<HTMLButtonElement>) => void;
  }
>(function ModeSegment(
  { "data-testid": testId, label, active, onSelect, onKeyDown },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      role="radio"
      data-testid={testId}
      aria-checked={active}
      tabIndex={active ? 0 : -1}
      data-state={active ? "on" : "off"}
      onClick={onSelect}
      onKeyDown={onKeyDown}
      className={cx(
        "relative z-[2] h-full shrink-0 cursor-pointer rounded-[10px] border-0 bg-transparent px-2.5 text-sm font-medium transition-colors duration-200 motion-reduce:transition-none",
        active
          ? "text-puntt-neutral-gray-12"
          : "text-puntt-neutral-gray-10 hover:text-puntt-neutral-gray-12",
      )}
    >
      {label}
    </button>
  );
});
